import React, { useCallback, useMemo } from "react";
import { useField } from "formik";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import {
    CounterpartyCombobox,
    CounterpartyComboboxValue,
} from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { TransactionRuleActionFormSchema } from "../constants";

interface Props {
    index: number;
    controlButtonSize?: TriggerButtonProps["size"];
}

export const CounterpartyActionForm: React.FC<Props> = ({
    index,
    controlButtonSize,
}) => {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);
    const { data: counterparties } = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
        includeUsageStats: false,
    });

    const selectedCounterparty = useMemo(() => {
        const counterpartyId = field.value?.counterpartyId;
        if (!counterpartyId) {
            return undefined;
        }

        return counterparties?.data?.find(
            (row) => row.counterparty.id === counterpartyId,
        )?.counterparty;
    }, [counterparties?.data, field.value?.counterpartyId]);

    const onChange = useCallback(
        (counterparty: CounterpartyComboboxValue | undefined) => {
            helpers.setValue({
                counterpartyId: counterparty?.id,
            });
        },
        [helpers],
    );

    return (
        <CounterpartyCombobox
            selectedCounterparty={selectedCounterparty}
            onChange={onChange}
            hideFooter
            triggerButtonProps={{
                variant: "primary",
                size: controlButtonSize ?? "md",
            }}
        />
    );
};
